import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { BotDifficulty } from "@guandan/bot-engine";
import { DEFAULT_RULE_PROFILE } from "@guandan/game-core";
import type { RoomSettings } from "@guandan/protocol";
import { GlassPanel, Toast } from "@guandan/ui";
import { AvatarCreator } from "./components/AvatarCreator";
import {
  AuthFlow,
  LegacyNicknamePasswordSetup,
} from "./components/AuthFlow";
import { FriendRooms, type RoomInvitation } from "./components/FriendRooms";
import { FriendsScreen } from "./components/FriendsScreen";
import { GameTable, type TableCompletion } from "./components/GameTable";
import {
  Matchmaking,
  RankingsScreen,
  RecordsScreen,
  SettingsScreen,
  SettlementScreen,
} from "./components/FeatureScreens";
import { Lobby, type LobbyRoute } from "./components/Lobby";
import {
  createPlatformService,
  type ActiveRoomView,
  type LobbyRoom,
} from "./lib/platform-api";
import { createPlayerDataClient } from "./lib/player-data";
import {
  clearSession,
  getSession,
  heartbeatSession,
  revokeSession,
  saveSession,
  type PlayerSession,
} from "./lib/session";
import { getNicknameStatus } from "./lib/nickname-auth";
import { playerStore, type MatchRecord } from "./lib/storage";
import { registerPwa } from "./lib/pwa";

type Phase =
  | "launch"
  | "auth"
  | "password-upgrade"
  | "avatar"
  | "app"
  | "matching"
  | "game"
  | "settlement";
type ActiveRoomCheckState = "idle" | "checking" | "ready" | "failed";

export default function App() {
  const [stored, setStored] = useState(() => playerStore.snapshot());
  const [phase, setPhase] = useState<Phase>("launch");
  const [route, setRoute] = useState<LobbyRoute>("lobby");
  const [pendingEmail, setPendingEmail] = useState("");
  const [pendingNickname, setPendingNickname] = useState("");
  const [roomId, setRoomId] = useState("practice-local");
  const [roomCode, setRoomCode] = useState<string | null>(null);
  const [gameMode, setGameMode] = useState<MatchRecord["mode"]>("电脑练习");
  const [botDifficulty, setBotDifficulty] = useState<BotDifficulty>("expert");
  const [tableOptions, setTableOptions] = useState<
    Pick<RoomSettings, "allowPause" | "voiceMode" | "ruleProfile">
  >({
    allowPause: true,
    voiceMode: "push-to-talk",
    ruleProfile: DEFAULT_RULE_PROFILE,
  });
  const [lastRecord, setLastRecord] = useState<MatchRecord | null>(null);
  const [rematchRoom, setRematchRoom] = useState<LobbyRoom | null>(null);
  const [rematchBusy, setRematchBusy] = useState(false);
  const [rematchError, setRematchError] = useState("");
  const rematchRequest = useRef<{
    sourceRoomId: string;
    requestId: string;
  } | null>(null);
  const [online, setOnline] = useState(navigator.onLine);
  const [offlineShell, setOfflineShell] = useState(false);
  const [activeRoom, setActiveRoom] = useState<ActiveRoomView | null>(null);
  const [activeRoomCheckState, setActiveRoomCheckState] =
    useState<ActiveRoomCheckState>("idle");
  const [activeRoomCheckEpoch, setActiveRoomCheckEpoch] = useState(0);
  const [toast, setToast] = useState("");
  const [invitation, setInvitation] = useState<RoomInvitation | null>(() =>
    invitationFromLocation(),
  );

  useEffect(() => {
    let active = true;
    const isLaunchActive = () => active;
    const restoreAuthenticatedLaunch = async () => {
      const networkAvailable = await hasNetworkConnectivity();
      if (!isLaunchActive()) return;
      setOnline(networkAvailable);
      const session = getSession();
      const localProfile = playerStore.snapshot().profile;
      if (!session) {
        if (!networkAvailable && localProfile) {
          setOfflineShell(true);
          setPhase("app");
        } else setPhase("auth");
        return;
      }
      setOfflineShell(false);
      const client = createPlayerDataClient();
      if (
        !networkAvailable &&
        localProfile?.email.toLowerCase() === session.email.toLowerCase()
      ) {
        setPhase("app");
        return;
      }
      if (!client) {
        if (!active) return;
        if (localProfile?.email.toLowerCase() === session.email.toLowerCase())
          setPhase("app");
        else {
          setPendingEmail(session.email);
          setPendingNickname(session.nickname ?? "");
          setPhase("avatar");
        }
        return;
      }
      try {
        const remote = await client.profile();
        if (!active) return;
        if (remote) {
          playerStore.restoreProfile(remote);
          if (await needsLegacyPasswordUpgrade(session, remote.nickname)) {
            if (!isLaunchActive()) return;
            setPendingNickname(remote.nickname);
            setPhase("password-upgrade");
            return;
          }
          setPhase("app");
        } else {
          setPendingEmail(session.email);
          setPendingNickname(session.nickname ?? "");
          setPhase("avatar");
        }
      } catch {
        if (!active) return;
        clearSession();
        setToast("登录会话无法恢复，请重新输入昵称");
        setPhase("auth");
      }
    };
    const timer = window.setTimeout(() => {
      void restoreAuthenticatedLaunch();
    }, 720);
    const sync = () => setStored(playerStore.snapshot());
    const onOnline = () => {
      setOnline(true);
      setToast("网络已恢复，牌桌会自动同步权威状态");
    };
    const onOffline = () => {
      setOnline(false);
      setToast("当前离线，已保留离线壳与本地练习");
    };
    window.addEventListener("tongxin-state", sync);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    const unregister = registerPwa(() =>
      setToast("发现新版本，重新打开应用即可更新"),
    );
    return () => {
      active = false;
      window.clearTimeout(timer);
      window.removeEventListener("tongxin-state", sync);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      unregister();
    };
  }, []);

  useEffect(() => {
    if (!offlineShell || !online) return;
    setOfflineShell(false);
    setRoute("lobby");
    setPhase("auth");
    setToast("网络已恢复，请重新登录后使用联网功能");
  }, [offlineShell, online]);

  useEffect(() => {
    if (!offlineShell || online) return;
    let active = true;
    const checkConnectivity = async () => {
      if (await hasNetworkConnectivity()) {
        if (active) setOnline(true);
      }
    };
    const timer = window.setInterval(() => {
      void checkConnectivity();
    }, 1_000);
    return () => {
      active = false;
      window.clearInterval(timer);
    };
  }, [offlineShell, online]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (
      !online ||
      offlineShell ||
      !["app", "matching", "game", "settlement"].includes(phase)
    )
      return;
    let controller = new AbortController();
    const heartbeat = () => {
      if (document.visibilityState !== "visible") return;
      controller.abort();
      controller = new AbortController();
      void heartbeatSession(controller.signal);
    };
    heartbeat();
    const timer = window.setInterval(heartbeat, 60_000);
    document.addEventListener("visibilitychange", heartbeat);
    return () => {
      controller.abort();
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", heartbeat);
    };
  }, [offlineShell, online, phase]);

  useEffect(() => {
    if (
      phase === "app" &&
      stored.profile &&
      invitation &&
      online &&
      !offlineShell &&
      activeRoomCheckState === "ready" &&
      !activeRoom
    )
      setRoute("friend-room");
  }, [
    activeRoom,
    activeRoomCheckState,
    invitation,
    offlineShell,
    online,
    phase,
    stored.profile,
  ]);

  useEffect(() => {
    const currentProfile = stored.profile;
    if (phase !== "app" || !currentProfile) {
      setActiveRoom(null);
      setActiveRoomCheckState("idle");
      return;
    }
    if (!online || offlineShell) {
      setActiveRoom(null);
      setActiveRoomCheckState("ready");
      return;
    }
    let active = true;
    setActiveRoomCheckState("checking");
    void createPlatformService(currentProfile)
      .activeRoom()
      .then((room) => {
        if (!active) return;
        setActiveRoom(room);
        setActiveRoomCheckState("ready");
      })
      .catch(() => {
        if (!active) return;
        setActiveRoomCheckState("failed");
        setToast("未完成牌局检查失败，请重试后再开始联网牌局");
      });
    return () => {
      active = false;
    };
  }, [activeRoomCheckEpoch, offlineShell, online, phase, stored.profile]);

  useLayoutEffect(() => {
    const previous = document.documentElement.style.fontSize;
    document.documentElement.style.fontSize = `${16 * stored.settings.textScale}px`;
    return () => {
      document.documentElement.style.fontSize = previous;
    };
  }, [stored.settings.textScale]);

  const profile = stored.profile;
  const navigate = useCallback(
    (next: LobbyRoute) => {
      if (
        (!navigator.onLine || offlineShell) &&
        (next === "friend-room" || next === "rankings")
      ) {
        setToast("离线时仅开放大厅壳、本机设置、头像与历史记录");
        return;
      }
      if (
        next === "friend-room" &&
        (activeRoomCheckState !== "ready" || activeRoom)
      ) {
        setToast(
          activeRoom
            ? "已有未完成房间，请先点击继续牌局"
            : "正在检查未完成牌局，请稍候",
        );
        return;
      }
      setRoute(next);
      setPhase("app");
    },
    [activeRoom, activeRoomCheckState, offlineShell],
  );
  const openGame = useCallback(
    (
      nextRoom: string,
      mode: MatchRecord["mode"],
      displayCode?: string,
      options?: Pick<
        RoomSettings,
        "allowPause" | "voiceMode" | "ruleProfile"
      >,
    ) => {
      setRoomId(nextRoom);
      setRoomCode(displayCode ?? null);
      setGameMode(mode);
      setTableOptions(
        options ?? {
          allowPause: true,
          voiceMode: "push-to-talk",
          ruleProfile: DEFAULT_RULE_PROFILE,
        },
      );
      setPhase("game");
    },
    [],
  );
  const openPractice = useCallback(
    (difficulty: BotDifficulty) => {
      setBotDifficulty(difficulty);
      openGame(`practice-${difficulty}-${Date.now()}`, "电脑练习");
    },
    [openGame],
  );
  const resumeActiveRoom = useCallback(
    (room: ActiveRoomView) => {
      if (room.status === "waiting") {
        setInvitation({ code: room.code });
        setRoute("friend-room");
        setPhase("app");
        return;
      }
      if (room.status !== "playing") {
        setActiveRoom(null);
        setActiveRoomCheckEpoch((value) => value + 1);
        return;
      }
      setBotDifficulty(room.settings.botDifficulty);
      openGame(
        room.id,
        room.settings.name.includes("匹配") ? "快速匹配" : "好友房",
        room.code,
        room.settings,
      );
    },
    [openGame],
  );
  const consumeInvitation = useCallback(() => {
    setInvitation(null);
    const url = new URL(location.href);
    url.searchParams.delete("room");
    url.searchParams.delete("invite");
    history.replaceState(null, "", `${url.pathname}${url.search}${url.hash}`);
  }, []);

  function finishGame(completion: TableCompletion) {
    const record = playerStore.addMatch(
      gameMode,
      completion.events,
      completion.snapshot,
      completion.authorityEvents,
      completion.revealedHandsByRound,
    );
    rematchRequest.current = null;
    setRematchRoom(null);
    setRematchBusy(false);
    setRematchError("");
    setActiveRoom(null);
    setLastRecord(record);
    setPhase("settlement");
    if (gameMode !== "电脑练习") void refreshAuthoritativeProfile();
  }

  async function endCurrentGame() {
    try {
      if (
        profile &&
        gameMode !== "电脑练习" &&
        online &&
        !offlineShell &&
        /^[0-9a-f]{8}-[0-9a-f-]{27}$/iu.test(roomId)
      ) {
        await createPlatformService(profile).endRoom(roomId);
      }
      setActiveRoom(null);
      setActiveRoomCheckEpoch((value) => value + 1);
      setRoute("lobby");
      setPhase("app");
      setToast("当前牌局已结束");
    } catch (cause) {
      throw new Error(
        cause instanceof Error && cause.message
          ? cause.message
          : "结束牌局失败，请重试",
      );
    }
  }

  async function rematchFriends() {
    if (!profile || !lastRecord || rematchBusy) return;
    const sourceRoomId = lastRecord.roomId;
    const request =
      rematchRequest.current?.sourceRoomId === sourceRoomId
        ? rematchRequest.current
        : { sourceRoomId, requestId: crypto.randomUUID() };
    rematchRequest.current = request;
    setRematchBusy(true);
    setRematchError("");
    try {
      const created = await createPlatformService(profile).rematchRoom(
        sourceRoomId,
        request.requestId,
      );
      setRematchRoom(created);
      setActiveRoom(created);
      setRoute("friend-room");
      setPhase("app");
    } catch (cause) {
      setRematchError(
        cause instanceof Error && cause.message
          ? cause.message
          : "原班续局创建失败，请重试",
      );
    } finally {
      setRematchBusy(false);
    }
  }

  async function refreshAuthoritativeProfile() {
    const client = createPlayerDataClient();
    if (!client) return;
    for (let attempt = 0; attempt < 3; attempt += 1) {
      try {
        const remote = await client.profile();
        if (remote) playerStore.restoreProfile(remote);
        return;
      } catch {
        if (attempt === 2) {
          setToast("牌局已权威结算；角色等级将在网络恢复后同步");
          return;
        }
        await new Promise((resolve) =>
          window.setTimeout(resolve, 250 * (attempt + 1)),
        );
      }
    }
  }

  async function restoreProfileAfterLogin(session: PlayerSession) {
    setOfflineShell(false);
    setPendingEmail(session.email);
    setPendingNickname(session.nickname ?? "");
    const client = createPlayerDataClient();
    if (!client) {
      const localProfile = playerStore.snapshot().profile;
      if (localProfile?.email.toLowerCase() === session.email.toLowerCase()) {
        setPhase("app");
        setRoute("lobby");
      } else setPhase("avatar");
      return;
    }
    const remote = await client.profile();
    if (!remote) {
      setPhase("avatar");
      return;
    }
    playerStore.restoreProfile(remote);
    setPhase("app");
    setRoute("lobby");
  }

  function completeLegacyPasswordUpgrade() {
    const session = getSession();
    if (session) saveSession({ ...session, kind: "nickname" });
    setPhase("app");
    setRoute("lobby");
    setToast("登录密码已设置，原昵称和战绩已保留");
  }

  async function createProfile(
    nickname: string,
    avatar: Parameters<typeof playerStore.saveProfile>[2],
  ) {
    setOfflineShell(false);
    const client = createPlayerDataClient();
    if (client) {
      const remote = await client.saveProfile(nickname, avatar);
      playerStore.restoreProfile(remote);
      setToast("角色资料已同步");
    } else {
      playerStore.saveProfile(pendingEmail, nickname, avatar);
      setToast("角色资料已保存到本机");
    }
    setPhase("app");
    setRoute("lobby");
  }

  async function updateAvatar(
    nickname: string,
    avatar: Parameters<typeof playerStore.saveAvatar>[0],
  ) {
    const client = createPlayerDataClient();
    if (client) {
      const remote = await client.saveProfile(nickname, avatar);
      playerStore.restoreProfile(remote);
      setToast("昵称与头像已同步");
    } else {
      playerStore.saveProfile(profile?.email ?? pendingEmail, nickname, avatar);
      setToast("昵称与头像已保存到本机");
    }
    navigate("lobby");
  }

  async function logout() {
    const revoked = await revokeSession();
    setOfflineShell(false);
    setActiveRoom(null);
    setPhase("auth");
    setRoute("lobby");
    setToast(
      revoked ? "已安全退出登录" : "本机已退出；服务端会话将在有效期结束后失效",
    );
  }

  const appStyle = {
    "--user-text-scale": String(stored.settings.textScale),
  } as React.CSSProperties;
  return (
    <div className="app-root" style={appStyle}>
      {!online ? (
        <div className="network-banner" role="status">
          离线模式：大厅资料仍可查看，联网后自动恢复牌局入口。
        </div>
      ) : null}
      {phase === "launch" ? <LaunchScreen /> : null}
      {phase === "auth" ? (
        <AuthFlow onVerified={restoreProfileAfterLogin} />
      ) : null}
      {phase === "password-upgrade" ? (
        <LegacyNicknamePasswordSetup
          nickname={pendingNickname}
          onCompleted={completeLegacyPasswordUpgrade}
        />
      ) : null}
      {phase === "avatar" ? (
        <AvatarCreator
          initialNickname={pendingNickname}
          nicknameLocked={getSession()?.kind === "nickname"}
          onSave={createProfile}
        />
      ) : null}
      {phase === "app" && profile && route === "lobby" ? (
        <Lobby
          profile={profile}
          offline={!online || offlineShell}
          activeRoom={activeRoom}
          activeRoomCheckState={activeRoomCheckState}
          onNavigate={navigate}
          onQuickMatch={() => {
            if (activeRoomCheckState !== "ready" || activeRoom) {
              setToast(
                activeRoom
                  ? "已有未完成牌局，请先点击继续牌局"
                  : "正在检查未完成牌局，请稍候",
              );
              return;
            }
            setPhase("matching");
          }}
          onResume={() => {
            if (activeRoom) resumeActiveRoom(activeRoom);
          }}
          onRetryActiveRoom={() =>
            setActiveRoomCheckEpoch((value) => value + 1)
          }
        />
      ) : null}
      {phase === "app" && profile && route === "friend-room" ? (
        <FriendRooms
          profile={profile}
          canCreateVoiceRoom={
            getSession()?.email.toLowerCase() === "45088195@qq.com"
          }
          invitation={invitation}
          initialRoom={rematchRoom}
          onInitialRoomConsumed={() => setRematchRoom(null)}
          onInvitationConsumed={consumeInvitation}
          onBack={() => navigate("lobby")}
          onStart={(nextRoom, displayCode, settings) => {
            setBotDifficulty(settings.botDifficulty);
            openGame(nextRoom, "好友房", displayCode, settings);
          }}
        />
      ) : null}
      {phase === "app" && profile && route === "friends" ? (
        <FriendsScreen
          offline={!online || offlineShell}
          onBack={() => navigate("lobby")}
          onNavigate={navigate}
        />
      ) : null}
      {phase === "app" && profile && route === "records" ? (
        <RecordsScreen
          profile={profile}
          matches={stored.matches}
          onBack={() => navigate("lobby")}
          onNavigate={navigate}
        />
      ) : null}
      {phase === "app" && profile && route === "rankings" ? (
        <RankingsScreen profile={profile} onBack={() => navigate("lobby")} />
      ) : null}
      {phase === "app" && profile && route === "settings" ? (
        <SettingsScreen
          profile={profile}
          settings={stored.settings}
          onChange={(settings) => playerStore.saveSettings(settings)}
          onBack={() => navigate("lobby")}
          onNavigate={navigate}
          onLogout={logout}
        />
      ) : null}
      {phase === "app" && profile && route === "dress" ? (
        <AvatarCreator
          initial={profile.avatar}
          initialNickname={profile.nickname}
          nicknameLocked={getSession()?.kind === "nickname"}
          onCancel={() => navigate("lobby")}
          onSave={updateAvatar}
        />
      ) : null}
      {phase === "matching" && profile ? (
        <Matchmaking
          profile={profile}
          onCancel={() => setPhase("app")}
          onMatched={(nextRoom, displayCode) =>
            openGame(nextRoom, "快速匹配", displayCode)
          }
        />
      ) : null}
      {phase === "game" && profile ? (
        <GameTable
          roomId={roomId}
          {...(roomCode ? { roomCode } : {})}
          mode={gameMode}
          botDifficulty={botDifficulty}
          allowPause={tableOptions.allowPause}
          ruleProfile={tableOptions.ruleProfile}
          initialVoiceMode={tableOptions.voiceMode}
          selfNickname={profile.nickname}
          selfAvatar={profile.avatar}
          settings={stored.settings}
          onSettingsChange={(settings) => playerStore.saveSettings(settings)}
          onExit={() => {
            setPhase("app");
            setRoute("lobby");
          }}
          onEnd={endCurrentGame}
          onFinish={finishGame}
        />
      ) : null}
      {phase === "settlement" && lastRecord ? (
        <SettlementScreen
          record={lastRecord}
          againLabel={
            gameMode === "电脑练习"
              ? "同难度再来一局"
              : gameMode === "快速匹配"
                ? "再次匹配"
                : "原班再来一局"
          }
          againBusy={rematchBusy}
          againError={rematchError}
          onAgain={() => {
            if (gameMode === "电脑练习") openPractice(botDifficulty);
            else if (gameMode === "快速匹配") setPhase("matching");
            else void rematchFriends();
          }}
          onReplay={() => navigate("records")}
          onLobby={() => navigate("lobby")}
        />
      ) : null}
      {toast ? <Toast>{toast}</Toast> : null}
    </div>
  );
}

async function hasNetworkConnectivity() {
  if (!navigator.onLine) return false;
  try {
    // No bearer/cookies; bypass browser and service-worker caches.
    await fetch("/api/connectivity-probe", {
      method: "HEAD",
      cache: "no-store",
      credentials: "omit",
    });
    return true;
  } catch {
    return false;
  }
}

async function needsLegacyPasswordUpgrade(
  session: PlayerSession,
  nickname: string,
): Promise<boolean> {
  if (
    session.kind === "nickname" ||
    session.kind === "owner" ||
    session.email.toLowerCase() === "45088195@qq.com"
  )
    return false;
  try {
    const status = await getNicknameStatus(nickname);
    return !status.registered;
  } catch {
    // A transient status failure must not invalidate an otherwise valid session.
    return false;
  }
}

function invitationFromLocation(): RoomInvitation | null {
  const parameters = new URLSearchParams(location.search);
  const code = parameters.get("room")?.replace(/\D/g, "").slice(0, 6) ?? "";
  if (code.length !== 6) return null;
  const token = parameters.get("invite") ?? undefined;
  return { code, ...(token ? { token } : {}) };
}

function LaunchScreen() {
  return (
    <main className="launch-screen">
      <div className="launch-mark" aria-hidden="true">
        <span>掼</span>
      </div>
      <h1>掼蛋高手</h1>
      <p>好友同桌，默契对家</p>
      <GlassPanel className="launch-status">
        <div className="loading-line" />
        <span>正在准备现代城市会客厅</span>
      </GlassPanel>
    </main>
  );
}
